// src/hooks/useAuth.js - COMPLETE with AuthProvider and useAuth hook
import { onAuthStateChanged } from 'firebase/auth';
import * as Sentry from '@sentry/browser';
import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { AutoCalendarProvisioningService } from '../services/AutoCalendarProvisioningService';
import { FirebaseService } from '../services/FirebaseService';
import { getTelephonyProfile } from '../services/data/telephonyProfile';
import { pingService } from '../services/TelephonyApi';

// ==========================================
// AUTH CONTEXT CREATION
// ==========================================

const AuthContext = createContext();

const telephonyHealthCheckedBusinesses = new Set();
const telephonyConnectivityChecks = new Set();

const telephonyConnectivityCheck = async (businessId) => {
  const key = businessId || 'anonymous';
  if (telephonyConnectivityChecks.has(key)) {
    return;
  }

  telephonyConnectivityChecks.add(key);

  try {
    const resp = await pingService();
    Sentry.addBreadcrumb({
      category: 'telephony.ping',
      level: 'info',
      data: { ok: resp?.pong === true, ts: resp?.ts },
      message: 'Cloud Run ping ok'
    });
  } catch (e) {
    Sentry.captureException(e, {
      tags: { feature: 'voice-sip-setup', endpoint: 'ping' },
      extra: { note: 'Cloud Run ping failed' }
    });
  }
};

const telephonyHealthCheck = async (businessId) => {
  if (!businessId || telephonyHealthCheckedBusinesses.has(businessId)) {
    return;
  }

  telephonyHealthCheckedBusinesses.add(businessId);

  try {
    const profile = await getTelephonyProfile(businessId);
    Sentry.addBreadcrumb({
      category: 'telephony',
      message: 'Telephony health check',
      level: 'info',
      data: {
        businessId,
        exists: Boolean(profile)
      }
    });
  } catch (error) {
    Sentry.withScope((scope) => {
      scope.setLevel('error');
      scope.setTag('telemetry', 'telephony-health-check');
      scope.setTag('businessId', businessId);
      scope.setContext('error', { message: error?.message });
      Sentry.captureException(error);
    });
  }
};

// ==========================================
// AUTH PROVIDER COMPONENT
// ==========================================

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pendingVerification, setPendingVerification] = useState(false);
  const [verificationEmail, setVerificationEmail] = useState(null);
  const [calendarProvisioning, setCalendarProvisioning] = useState(false);
  const [calendarProvisioningStatus, setCalendarProvisioningStatus] = useState(null);

  // ==========================================
  // AUTHENTICATION STATE LISTENER
  // ==========================================
  
  // ==========================================
  // AUTOMATIC CALENDAR PROVISIONING
  // ==========================================
  
  const checkAndProvisionCalendar = useCallback(async (businessData, userData) => {
    try {
      const businessId = businessData.businessId || businessData.business_id;
      
      if (!businessId) {
        console.warn('⚠️ No business ID available for calendar provisioning');
        return;
      }

      // Check if calendar provisioning is needed
      const provisioningCheck = await AutoCalendarProvisioningService.checkProvisioningNeeded(businessId);
      
      if (!provisioningCheck.needed) {
        console.log('✅ Calendar already integrated');
        setCalendarProvisioningStatus({
          status: 'completed',
          message: 'Calendar integration active'
        });
        return;
      }

      if (!provisioningCheck.canAutoProvision) {
        console.log('⚠️ Manual calendar setup required');
        setCalendarProvisioningStatus({
          status: 'manual_required',
          message: 'Manual calendar setup required',
          setupURL: '/dashboard/calendar-integration'
        });
        return;
      }

      // Start automatic calendar provisioning
      console.log('🔧 Starting automatic calendar provisioning...');
      setCalendarProvisioning(true);
      setCalendarProvisioningStatus({
        status: 'provisioning',
        message: 'Setting up your business calendar...'
      });

      // Perform automatic provisioning (non-blocking)
      try {
        const provisioningResult = await AutoCalendarProvisioningService.provisionDuringLogin(
          userData,
          businessData
        );

        if (provisioningResult.success) {
          console.log('✅ Calendar provisioned automatically');
          setCalendarProvisioningStatus({
            status: 'completed',
            message: 'Business calendar ready! Your appointments will sync automatically.',
            calendarId: provisioningResult.calendarId
          });
        } else {
          console.warn('⚠️ Automatic calendar setup failed:', provisioningResult.error);
          setCalendarProvisioningStatus({
            status: 'failed',
            message: 'Automatic setup failed - manual setup available',
            setupURL: '/dashboard/calendar-integration',
            error: provisioningResult.error
          });
        }
      } catch (provisioningError) {
        console.error('❌ Calendar provisioning error:', provisioningError);
        setCalendarProvisioningStatus({
          status: 'error',
          message: 'Calendar setup failed - manual setup available',
          setupURL: '/dashboard/calendar-integration',
          error: provisioningError.message
        });
      }
    } catch (error) {
      console.error('❌ Calendar provisioning check failed:', error);
      setCalendarProvisioningStatus({
        status: 'error',
        message: 'Unable to check calendar setup',
        error: error.message
      });
    } finally {
      setCalendarProvisioning(false);
    }
  }, [setCalendarProvisioning, setCalendarProvisioningStatus]);

  const resolveUser = useCallback(async (firebaseUser, { skipReload = false } = {}) => {
    if (!firebaseUser) {
      console.log('🔐 User signed out');
      setUser(null);
      setPendingVerification(false);
      setVerificationEmail(null);
      setCalendarProvisioning(false);
      setCalendarProvisioningStatus(null);
      setError(null);
      setLoading(false);
      return;
    }

    const { auth } = FirebaseService.getInstances();
    let currentUser = firebaseUser;

    if (!skipReload) {
      try {
        await firebaseUser.reload();
        currentUser = auth.currentUser || firebaseUser;
      } catch (reloadError) {
        console.warn('⚠️ Could not reload user for verification check:', reloadError);
      }
    }

    if (!currentUser.emailVerified) {
      console.log('📧 Awaiting email verification for user:', currentUser.uid);
      setPendingVerification(true);
      setVerificationEmail(currentUser.email || null);
      setUser(null);
      setCalendarProvisioning(false);
      setCalendarProvisioningStatus(null);
      setError(null);
      setLoading(false);
      return;
    }

    setPendingVerification(false);
    setVerificationEmail(null);

    try {
      const businessData = await FirebaseService.getUserBusinessData(
        currentUser.uid,
        { retries: 4, backoffMs: 300 }
      );

      const userData = {
        uid: currentUser.uid,
        email: currentUser.email,
        displayName: currentUser.displayName,
        businessId: businessData?.businessId || businessData?.business_id || null,
        businessData: businessData || null,
        emailVerified: true,
        hasBusiness: Boolean(businessData)
      };

      setUser(userData);
      setError(null);
      telephonyConnectivityCheck(userData.businessId);

      if (userData.businessId) {
        telephonyHealthCheck(userData.businessId);
      }

      if (businessData) {
        await checkAndProvisionCalendar(businessData, userData);
      } else {
        console.warn('⚠️ No business data found for user');
        setCalendarProvisioningStatus(null);
      }
    } catch (error) {
      console.error('❌ Auth state change error:', error);
      setError(error.message);
      setCalendarProvisioningStatus(null);
    } finally {
      setLoading(false);
    }
  }, [checkAndProvisionCalendar]);

  const refreshUser = useCallback(async () => {
    const reloadedUser = await FirebaseService.reloadCurrentUser();

    if (reloadedUser) {
      await resolveUser(reloadedUser, { skipReload: true });
    }
  }, [resolveUser]);

  useEffect(() => {
    const { auth } = FirebaseService.getInstances();

    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      resolveUser(firebaseUser);
    });

    return unsubscribe;
  }, [resolveUser]);

  useEffect(() => {
    if (!pendingVerification) {
      return;
    }

    const interval = setInterval(() => {
      refreshUser();
    }, 5000);

    return () => clearInterval(interval);
  }, [pendingVerification, refreshUser]);

  // ==========================================
  // AUTHENTICATION METHODS
  // ==========================================
  
    const login = async (email, password) => {
      try {
        setLoading(true); // Set loading true at start
        setError(null);
        setCalendarProvisioning(false);
        
        console.log('🔐 Attempting login with calendar provisioning...');
        
        // Standard authentication
        const result = await FirebaseService.signInWithEmailAndPassword(email, password);
        
        if (result.success) {
          console.log('✅ Authentication successful, waiting for user state to update...');
          
          // User state will be set by the auth listener above
          // Calendar provisioning will be handled automatically
          // Loading will be set to false by auth listener when user state is fully updated
          
          return {
            success: true,
            user: result.user,
            message: 'Login successful'
          };
        } else {
          setLoading(false); // Set loading false only on failure
          setError(result.error);
          return {
            success: false,
            error: result.error,
            message: 'Login failed'
          };
        }
      } catch (error) {
        console.error('❌ Login error:', error);
        setLoading(false); // Set loading false on error
        setError(error.message);
        return {
          success: false,
          error: error.message,
          message: 'Login failed'
        };
      }
      // Let auth listener handle setLoading(false) on success
    };

  const logout = async () => {
    try {
      setLoading(true);
      setError(null);
      
      await FirebaseService.signOut();
      
      // Clear all state
      setUser(null);
      setCalendarProvisioningStatus(null);
      
      return { success: true, message: 'Logged out successfully' };
    } catch (error) {
      console.error('❌ Logout error:', error);
      setError(error.message);
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  const register = async (userData, businessData) => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('📝 Starting registration process...');
      
      // Create user account and business profile
      const result = await FirebaseService.registerNewBusiness(userData, businessData);
      
      if (result.success) {
        console.log('✅ Registration successful');
        
        // User state will be set by the auth listener
        // Calendar provisioning will be handled automatically
        
        return {
          success: true,
          user: result.user,
          businessId: result.businessId,
          message: 'Registration successful'
        };
      } else {
        setError(result.error);
        return {
          success: false,
          error: result.error,
          message: 'Registration failed'
        };
      }
    } catch (error) {
      console.error('❌ Registration error:', error);
      setError(error.message);
      return {
        success: false,
        error: error.message,
        message: 'Registration failed'
      };
    } finally {
      setLoading(false);
    }
  };

  const updateProfile = async (profileData) => {
    try {
      setLoading(true);
      setError(null);
      
      if (!user?.businessId) {
        throw new Error('No business ID available');
      }

      const result = await FirebaseService.updateBusinessProfile(user.businessId, profileData);
      
      if (result.success) {
        // Update local user data
        setUser(prev => ({
          ...prev,
          businessData: { ...prev.businessData, ...profileData }
        }));
        
        return { success: true, message: 'Profile updated successfully' };
      } else {
        throw new Error(result.error);
      }
    } catch (error) {
      console.error('❌ Profile update error:', error);
      setError(error.message);
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  // ==========================================
  // CALENDAR PROVISIONING MANAGEMENT
  // ==========================================
  
  const retryCalendarProvisioning = async () => {
    if (!user?.businessId || !user?.businessData) {
      console.error('❌ Cannot retry provisioning - missing user data');
      return { success: false, error: 'User data not available' };
    }

    try {
      setCalendarProvisioning(true);
      setCalendarProvisioningStatus({
        status: 'provisioning',
        message: 'Retrying calendar setup...'
      });

      const result = await AutoCalendarProvisioningService.retryProvisioning(
        user.businessId,
        user.businessData
      );

      if (result.success) {
        setCalendarProvisioningStatus({
          status: 'completed',
          message: 'Calendar setup completed successfully!',
          calendarId: result.calendarId
        });
      } else {
        setCalendarProvisioningStatus({
          status: 'failed',
          message: 'Setup failed - manual configuration required',
          setupURL: '/dashboard/calendar-integration',
          error: result.error
        });
      }

      return result;
    } catch (error) {
      console.error('❌ Retry provisioning error:', error);
      setCalendarProvisioningStatus({
        status: 'error',
        message: 'Retry failed',
        error: error.message
      });
      return { success: false, error: error.message };
    } finally {
      setCalendarProvisioning(false);
    }
  };

  const dismissCalendarProvisioning = () => {
    setCalendarProvisioningStatus(null);
  };

  // ==========================================
  // CONTEXT VALUE
  // ==========================================
  
  const value = {
    // Authentication State
    user,
    loading,
    error,
    isAuthenticated: !!user,
    hasBusiness: !!user?.hasBusiness,
    pendingVerification,
    verificationEmail,

    // Calendar Provisioning State
    calendarProvisioning,
    calendarProvisioningStatus,
    isCalendarProvisioning: calendarProvisioning,
    
    // Authentication Actions
    login,
    logout,
    register,
    updateProfile,

    // Calendar Provisioning Actions
    retryCalendarProvisioning,
    dismissCalendarProvisioning,
    refreshUser,
    
    // Calendar Status Helpers
    hasCalendarIntegration: calendarProvisioningStatus?.status === 'completed',
    needsCalendarSetup: calendarProvisioningStatus?.status === 'manual_required' || 
                       calendarProvisioningStatus?.status === 'failed',
    calendarSetupURL: calendarProvisioningStatus?.setupURL || '/dashboard/calendar-integration',

    // Utility Functions
    clearError: () => setError(null),
    setError: (errorMessage) => setError(errorMessage)
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// ==========================================
// useAuth HOOK
// ==========================================

export const useAuth = () => {
  const context = useContext(AuthContext);
  
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  
  return context;
};

// ==========================================
// EXPORTS
// ==========================================

export default AuthProvider;
